/**
 * FLOYD 2026-10-26 (DEFECT 2) — the browser's read of the started-work carry window.
 *
 * Reads exactly what create-eod-rollover reads (the ticket's day and start, the technician's dated hours, time off,
 * breaks and partial absences, the shop's holidays and time zone) and runs the SHARED planner
 * (`supabase/functions/_shared/startedWorkCarryWindow.ts`). The modal's preflight therefore asks the same question
 * the server will answer — it never parses an "HH:mm" out of whatever the opener passed, never applies a time to
 * the browser's own calendar day, and never invents a next window.
 *
 * Unknown stays unknown: an unreadable ticket, technician or holiday list is a `missing` answer, and the run-long
 * planner refuses it for missing context — exactly the protection it exists to give.
 */
import { supabase } from '@/integrations/supabase/client';
import { calculateWallMinutes } from '../../../supabase/functions/_shared/canonicalTimeMath.ts';
import { resolvePlanningBufferPct } from '../../../supabase/functions/_shared/schedulingDay.ts';
import { getMinutesInShopTZ } from '../../../supabase/functions/_shared/timezoneMath.ts';
import { getTodayInShopTz } from '../../../supabase/functions/_shared/shopTime.ts';
import { loadTechnicianWorkDayResolver } from '../../../supabase/functions/_shared/rolloverWorkDay.ts';
import type { ResolveWorkDay } from '../../../supabase/functions/_shared/rolloverSegmentPlanner.ts';
import {
  planStartedWorkCarry,
  resolveCarryShopTimezone,
  type StartedWorkCarryWindow,
} from '../../../supabase/functions/_shared/startedWorkCarryWindow.ts';

export type { StartedWorkCarryWindow };

/** What the server would plan with — everything but the remaining minutes, which the person may still state. */
export interface StartedWorkCarryContext {
  ticketId: string;
  shopTimezone: string;
  /** The work's own shop-local date (tickets.scheduled_date). */
  scheduledDate: string;
  /** The shop's today, when the context was read. */
  shopToday: string;
  /** True when the work's own day is already over in shop time (a decision made on a later day). */
  stale: boolean;
  startMin: number;
  optiTime: number;
  techName: string;
  resolveDay: ResolveWorkDay;
  flatShiftEnd: string | null;
  recurringBreaks: unknown;
  nonRecurringBreaks: unknown;
}

export type StartedWorkCarryContextResult =
  | { state: 'ready'; context: StartedWorkCarryContext }
  | { state: 'missing'; reason: 'ticket_unreadable' | 'ticket_unscheduled' | 'technician_unreadable' | 'schedule_unreadable'
      /** The shop's time zone is missing, unreadable or not a real zone — never guessed (SHOP_TIMEZONE_UNKNOWN). */
      | 'shop_timezone_unknown' };

export async function loadStartedWorkCarryContext(
  ticketId: string,
  client: typeof supabase = supabase,
): Promise<StartedWorkCarryContextResult> {
  const { data: ticket, error: ticketError } = await client
    .from('tickets')
    .select('id, shop_id, assigned_tech_id, scheduled_date, start_time, predicted_minutes')
    .eq('id', ticketId)
    .maybeSingle();
  if (ticketError || !ticket) return { state: 'missing', reason: 'ticket_unreadable' };
  const t = ticket as { shop_id: string; assigned_tech_id: string | null; scheduled_date: string | null; start_time: string | null; predicted_minutes: number | null };
  if (!t.assigned_tech_id || !t.scheduled_date || !t.start_time) return { state: 'missing', reason: 'ticket_unscheduled' };

  const [{ data: tech, error: techError }, { data: profile, error: profileError }] = await Promise.all([
    client.from('technicians')
      .select('id, name, shift_end, efficiency_percentage, buffer_percentage, recurring_breaks, non_recurring_breaks')
      .eq('id', t.assigned_tech_id).maybeSingle(),
    client.from('shop_profile').select('time_zone').eq('shop_id', t.shop_id).maybeSingle(),
  ]);
  if (techError || !tech) return { state: 'missing', reason: 'technician_unreadable' };
  const tc = tech as { name?: string | null; shift_end?: string | null; efficiency_percentage?: number | null;
    buffer_percentage?: number | null; recurring_breaks?: unknown; non_recurring_breaks?: unknown };

  let resolveDay: ResolveWorkDay;
  try {
    // strict: an unreadable holiday or absence list refuses rather than planning a continuation onto a holiday.
    resolveDay = await loadTechnicianWorkDayResolver(
      client, { shopId: t.shop_id, techId: t.assigned_tech_id, fromDateStr: t.scheduled_date }, { strict: true });
  } catch {
    return { state: 'missing', reason: 'schedule_unreadable' };
  }

  // The same rule as create-eod-rollover: an unknown zone refuses the carry; it is never planned in another zone.
  const shopTimezone = profileError ? null : resolveCarryShopTimezone((profile as { time_zone?: string | null } | null)?.time_zone);
  if (!shopTimezone) return { state: 'missing', reason: 'shop_timezone_unknown' };
  const shopToday = getTodayInShopTz(shopTimezone);
  return {
    state: 'ready',
    context: {
      ticketId,
      shopTimezone,
      scheduledDate: t.scheduled_date,
      shopToday,
      stale: t.scheduled_date < shopToday,
      startMin: getMinutesInShopTZ(t.start_time, shopTimezone),
      optiTime: calculateWallMinutes(Number(t.predicted_minutes ?? 0), tc.efficiency_percentage || 100,
        resolvePlanningBufferPct(tc.buffer_percentage)),
      techName: String(tc.name ?? ''),
      resolveDay,
      flatShiftEnd: tc.shift_end ?? null,
      recurringBreaks: tc.recurring_breaks,
      nonRecurringBreaks: tc.non_recurring_breaks,
    },
  };
}

/** The carry the server would build for this context, with the person's stated remainder (if any). */
export function planCarryFromContext(ctx: StartedWorkCarryContext, remainingMinutesOverride: number | null): StartedWorkCarryWindow {
  return planStartedWorkCarry({
    scheduledDate: ctx.scheduledDate,
    shopToday: ctx.shopToday,
    shopTimezone: ctx.shopTimezone,
    resolveDay: ctx.resolveDay,
    startMin: ctx.startMin,
    optiTime: ctx.optiTime,
    flatShiftEnd: ctx.flatShiftEnd,
    recurringBreaks: ctx.recurringBreaks,
    nonRecurringBreaks: ctx.nonRecurringBreaks,
    remainingMinutesOverride,
  });
}

/**
 * The end-of-day instant the after-hours check is measured against.
 *
 * The server's authorized boundary (`detection.boundary_at` — the work day's end including any AUTHORIZED
 * overtime) is used when it is a real instant on the work's own shop-local day; otherwise the dated normal-hours
 * end. A boundary that does not parse, or that belongs to another day, is never applied to this one.
 */
export function resolveAuthorizedBoundary(input: {
  normalEndAt: Date;
  authorizedBoundaryAt?: string | null;
  scheduledDate: string;
  shopTimezone: string;
}): { at: Date; source: 'authorized_boundary' | 'dated_shift_end' } {
  const ms = Date.parse(String(input.authorizedBoundaryAt ?? ''));
  if (Number.isFinite(ms)) {
    const at = new Date(ms);
    // The authorized boundary may fall just after midnight of a late shift; it belongs to the work's day when it is
    // within that day or the morning after (an overtime window never spans more than one night).
    const dayOf = shopDateOf(at, input.shopTimezone);
    if (dayOf === input.scheduledDate || (dayOf > input.scheduledDate && ms - input.normalEndAt.getTime() <= 12 * 3_600_000
        && ms >= input.normalEndAt.getTime())) {
      return { at, source: 'authorized_boundary' };
    }
  }
  return { at: input.normalEndAt, source: 'dated_shift_end' };
}

function shopDateOf(at: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** "Tue Oct 20, 10:00 PM" — an instant in the SHOP's time, for the person deciding. */
export function formatShopDayTime(at: Date, timezone: string): string {
  const day = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short', month: 'short', day: 'numeric' }).format(at);
  const time = new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit' }).format(at);
  return `${day.replace(',', '')}, ${time}`;
}
